import { useRef, useState, type PointerEvent, type ReactNode, type RefObject } from 'react';
import { Dialog as Base } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { cn } from './cn';
import { DialogCloseProvider, useDeferredClose, type CloseRequest } from './deferred-close';
import { Drawer } from './Drawer';
import { escapeCloses, useEscapeAnywhere } from './escape';
import { returnFocusUnlessMoved } from './focus';
import { Icon } from './icons';
import { IconButton } from './IconButton';

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

/**
 * Modal dialog on Base UI: focus moves in and is trapped, Escape and the backdrop close it, focus returns to the
 * element that opened it. Rendered open: the caller mounts it to open and unmounts it in `onClose`, which runs once the
 * exit animation is over (P18). Inside, `useDialogClose()` / `DialogClose` close it with that animation.
 */
export function Dialog({
  title,
  description,
  onClose,
  onCloseRequest,
  children,
  footer,
  size = 'sm',
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  /** Asked before every close (Escape, backdrop, `DialogClose`); `false` keeps the dialog open (a running import aborts instead). */
  onCloseRequest?: CloseRequest;
  children?: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const popup = useRef<HTMLDivElement>(null);
  // Read while rendering the first time, before a field inside takes the focus (autoFocus).
  const [opener] = useState(() => document.activeElement);
  const { open, request, complete } = useDeferredClose(onClose, { onCloseRequest });
  return (
    <DialogCloseProvider value={request}>
      <Base.Root
        open={open}
        onOpenChange={(next, details) => {
          if (!next && !request()) details.cancel();
        }}
        onOpenChangeComplete={complete}
      >
        <Base.Portal>
          <Base.Backdrop data-backdrop="" className="os-fade fixed inset-0 z-50 bg-overlay" />
          <Base.Popup
            ref={popup}
            finalFocus={returnFocusUnlessMoved(popup, opener)}
            {...{ [CANVAS_UI]: '' }}
            className={cn(
              'os-pop fixed left-1/2 top-1/2 z-50 max-h-[85dvh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl bg-chrome p-6 font-sans text-sm text-fg shadow-float outline-none',
              SIZES[size],
              className,
            )}
          >
            <Base.Title className="mb-4 font-display text-title font-semibold text-fg">{title}</Base.Title>
            {description && <Base.Description className="-mt-3 mb-4 text-sm text-fg-muted">{description}</Base.Description>}
            {children}
            {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
          </Base.Popup>
        </Base.Portal>
      </Base.Root>
    </DialogCloseProvider>
  );
}

/** Drag on the handle that closes a sheet: 64 px down, or a flick faster than 0.5 px/ms (P16-2 Ruling 6). */
export const SWIPE_CLOSE_PX = 64;
export const SWIPE_CLOSE_SPEED = 0.5;

/**
 * Swipe down on the handle of the non-modal sheet. The finger's offset goes into `--sheet-drag`, which `.os-sheet` reads
 * in `translate` (the property its enter and exit transitions move: an inline `transform` fought them, and cleared before
 * the exit it made the sheet jump back). A release that closes keeps the offset, so the exit starts from where the finger
 * let go; a release that does not closes the gap with the normal transition.
 */
function useSwipeDown(popup: RefObject<HTMLElement | null>, onClose: () => void) {
  const start = useRef<{ id: number; y: number; t: number } | null>(null);
  const move = (dy: number) => {
    const el = popup.current;
    if (!el) return;
    if (dy > 0) {
      el.style.setProperty('--sheet-drag', `${dy}px`);
      el.setAttribute('data-swiping', '');
    } else {
      el.style.removeProperty('--sheet-drag');
      el.removeAttribute('data-swiping');
    }
  };
  return {
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      start.current = { id: e.pointerId, y: e.clientY, t: performance.now() };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      if (start.current?.id === e.pointerId) move(e.clientY - start.current.y);
    },
    onPointerUp: (e: PointerEvent<HTMLElement>) => {
      const s = start.current;
      start.current = null;
      if (!s || s.id !== e.pointerId) return;
      const dy = e.clientY - s.y;
      const speed = dy / Math.max(1, performance.now() - s.t);
      if (dy >= SWIPE_CLOSE_PX || speed > SWIPE_CLOSE_SPEED) {
        popup.current?.removeAttribute('data-swiping');
        onClose();
      } else move(0);
    },
    onPointerCancel: () => {
      start.current = null;
      move(0);
    },
  };
}

/**
 * Sheet docked to the bottom of the screen (phones, spec §13). Its close button, Escape and a swipe down on the handle
 * close it (P16-2 Ruling 6).
 * `modal` (the phone toolbar's "+" sheet, P5f): a P18 `Drawer` (backdrop, focus trapped, a tap outside closes it, the
 * sheet follows the finger and leaves with the speed of the flick); same props and accessible names as before.
 * Non-modal (the dashboard's navigation sheet): the page behind stays usable and a tap there leaves it open; it enters
 * and leaves with the same animation as the others.
 * `role="region"` with `initialFocus={false}`: a sheet that comes and goes with the selection (the phone's properties)
 * and never takes the focus from the canvas or the edited text. It is mounted open and closes at once: its owner drops it
 * with the selection, so there is no exit to wait for.
 */
export function Sheet({
  title,
  closeLabel,
  onClose,
  children,
  className,
  modal = false,
  role,
  initialFocus,
  finalFocus,
}: {
  title: ReactNode;
  closeLabel: string;
  onClose: () => void;
  children?: ReactNode;
  className?: string;
  modal?: boolean;
  role?: 'region';
  initialFocus?: false;
  finalFocus?: false;
}) {
  if (modal)
    return (
      // Today's size: a "+" sheet is never taller than 60 dvh (the Drawer's own cap is the screen).
      <Drawer
        title={title}
        closeLabel={closeLabel}
        onClose={onClose}
        className={cn('max-h-[60dvh]!', className)}
        initialFocus={initialFocus}
        finalFocus={finalFocus}
      >
        {children}
      </Drawer>
    );
  return (
    <DockedSheet
      title={title}
      closeLabel={closeLabel}
      onClose={onClose}
      className={className}
      role={role}
      initialFocus={initialFocus}
      finalFocus={finalFocus}
    >
      {children}
    </DockedSheet>
  );
}

function DockedSheet({
  title,
  closeLabel,
  onClose,
  children,
  className,
  role,
  initialFocus,
  finalFocus,
}: {
  title: ReactNode;
  closeLabel: string;
  onClose: () => void;
  children?: ReactNode;
  className?: string;
  role?: 'region';
  initialFocus?: false;
  finalFocus?: false;
}) {
  const popup = useRef<HTMLDivElement>(null);
  const [opener] = useState(() => document.activeElement);
  const { open, request, complete } = useDeferredClose(onClose, { animate: role !== 'region' });
  const close = useEscapeAnywhere(popup, () => void request());
  const swipe = useSwipeDown(popup, close);
  return (
    <DialogCloseProvider value={request}>
        <Base.Root
          open={open}
          modal={false}
          disablePointerDismissal
          onOpenChange={(next, details) => {
            if (next) return;
            if (details.reason === 'escape-key' && !escapeCloses(details.event, popup.current))
              return details.cancel();
            close();
          }}
          onOpenChangeComplete={complete}
        >
          <Base.Portal>
            <Base.Popup
              ref={popup}
              {...{ [CANVAS_UI]: '' }}
              initialFocus={initialFocus ?? popup}
              finalFocus={finalFocus ?? returnFocusUnlessMoved(popup, opener)}
              {...(role ? { role } : {})}
              className={cn(
                'os-sheet fixed inset-x-0 bottom-0 z-40',
                'max-h-[60dvh] overflow-y-auto rounded-t-3xl bg-chrome px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-1 font-sans text-sm text-fg shadow-float outline-none',
                className,
              )}
            >
              <div
                aria-hidden="true"
                data-testid="sheet-handle"
                className="mx-auto flex h-6 w-16 touch-none items-center justify-center"
                {...swipe}
              >
                <span className="block h-1 w-9 rounded-full bg-border-strong" />
              </div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <Base.Title className="text-xs font-medium text-fg-muted">{title}</Base.Title>
                <IconButton
                  label={closeLabel}
                  icon={<Icon icon={X} size={16} />}
                  size="sm"
                  onClick={close}
                />
              </div>
              {children}
            </Base.Popup>
          </Base.Portal>
        </Base.Root>
    </DialogCloseProvider>
  );
}
